import type {
  ConciseAmendOperation,
  ConciseAnnulOperation,
  ConciseCreateOperation,
  ConciseHideOperation,
} from '@festina/configuration-general-types/configuration'
import { dateUtils, type Optional } from '@festina-life/core'
import cx from 'classnames'
import type { ComponentProps } from 'react'

import { enumDefinitionPath, enumLiteralPath } from '../../../../system/paths'
import { useAnnulOperationDescriptors } from '../../../../uiModels/annulOperations/useAnnulOperationDescriptors'
import { operationLinkClassName } from '../../../configurationOperationLink/helpers'
import { LocationAwareOperationLink } from '../../../configurationOperationLink/LocationAwareOperationLink'
import {
  conciseOperationTypeToConfigurationItem,
  isConciseAnnulOperation,
} from './helpers'

const getPropertyPath = (
  entityName: Optional<string>,
  linkType: ReturnType<typeof conciseOperationTypeToConfigurationItem>,
) => {
  if (entityName) {
    if (linkType === 'enumLiteral') {
      const [enumName, literalName] = entityName.split('.')
      return enumLiteralPath({ enumName, literalName })
    } else if (linkType === 'enumDefinition') {
      return enumDefinitionPath(entityName)
    }
  }

  return undefined
}

type Props = {
  operation:
    | ConciseAmendOperation
    | ConciseAnnulOperation
    | ConciseCreateOperation
    | ConciseHideOperation
  commonLinkProps: Pick<
    ComponentProps<typeof LocationAwareOperationLink>,
    | 'shouldShowLocationIndicators'
    | 'layer'
    | 'block'
    | 'isLoading'
    | 'branchKey'
    | 'workspaceKey'
    | 'untilChangesetKey'
    | 'operationKey'
    | 'isDraft'
  >
}

export const ConciseChangesetOperationLink = ({
  operation,
  commonLinkProps,
}: Props) => {
  const descriptors = useAnnulOperationDescriptors()

  if (isConciseAnnulOperation(operation)) {
    return (
      <LocationAwareOperationLink
        {...commonLinkProps}
        type="annulOperation"
        iconType={
          operation.createOperationClassName
            ? descriptors[operation.createOperationClassName]
                ?.configurationItemVariant
            : undefined
        }
        itemId={operation.operationKey!}
        itemLabel={operation.name}
      />
    )
  }

  const linkType = conciseOperationTypeToConfigurationItem(operation)
  const propertyPath = getPropertyPath(operation.entityName, linkType)

  if (!linkType || !operation.entityName) {
    return (
      <div
        key={JSON.stringify(operation)}
        className={cx(
          operationLinkClassName({
            isInteractive: false,
            isDraft: false,
          }),
        )}
      >
        {operation.entityName}
      </div>
    )
  }

  return (
    <LocationAwareOperationLink
      {...commonLinkProps}
      type={linkType}
      itemId={
        linkType !== 'enumLiteral'
          ? operation.entityName
          : operation.entityName.split('.')[0]
      }
      itemLabel={linkType === 'enumLiteral' ? operation.entityName : undefined}
      fromDate={
        operation.entityFromDate
          ? dateUtils.dateUtcAsLocal(operation.entityFromDate).toDate()
          : undefined
      }
      toDate={
        operation.entityToDate
          ? dateUtils.dateUtcAsLocal(operation.entityToDate).toDate()
          : undefined
      }
      propertyPath={propertyPath}
    />
  )
}
